import React from 'react';
import { Link } from 'react-router-dom';
import { useThunk } from './useThunk';
import styles from './orderAutocomplete.module.scss';
import { Autocomplete, Box, TextField, Tooltip, Typography } from '@mui/material';

/**
 * OrderAutocomplete Component
 *
 * A reusable autocomplete component for searching orders by ID.
 * It encapsulates the fetching logic and handles debounce internally via useThunk hook.
 */
const OrderAutocomplete = ({
    value,
    onChange,
    onSelect,
    onAssignable,
    organisationId,
    setError,
    label = 'Order #',
    placeholder,
    required = false,
    ...rest
}) => {
    const {
        options,
        loading,
        inputValue,
        handleInputChange,
        assignedTicket,
        handleCheckAssignable,
    } = useThunk({ value, organisationId, setError, onAssignable });

    const handleInternalChange = (event, newValue) => {
        const id = newValue && typeof newValue === 'object' ? newValue.id : (newValue || '');
        onChange(String(id));
        if (onSelect) onSelect(newValue);
        handleCheckAssignable(id);
    };

    return (
        <Box>
            <Autocomplete
                {...rest}
                freeSolo
                options={options}
                loading={loading}
                inputValue={inputValue}
                noOptionsText={inputValue.length < 4 ? 'Type at least 4 characters' : 'No orders found'}
                getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.id ? String(option.id) : '';
                }}
                onInputChange={handleInputChange}
                onChange={handleInternalChange}
                renderInput={(params) => (
                    <Tooltip
                        title={!organisationId ? 'Please select an organisation first' : ''}
                        placement='top'
                        arrow
                    >
                        <TextField
                            {...params}
                            label={label}
                            required={required}
                            variant='outlined'
                            placeholder={!organisationId ? 'Select Organisation first' : (placeholder || 'Search Order #')}
                        />
                    </Tooltip>
                )}
            />
            {assignedTicket && (
                <Typography className={styles.warningText}>
                    An {assignedTicket.category ? ` ${assignedTicket.category}` : ''} ticket already associated to this order: <Link target='_blank' rel='noopener noreferrer'
                        to={`/admin/support/investigation/${assignedTicket.id}`}
                        className='rt-link'>{assignedTicket.id}</Link>
                </Typography>
            )}
        </Box>
    );
};

export default OrderAutocomplete;
